import { Box, Stack, Typography, Theme, useTheme } from '@mui/material';
import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import { useTranslation } from 'react-i18next';

import { IdleStatusProps } from './types';
import { idlePhase, IdlePhase } from './idlePhase';
import { ProgressBar } from '../../../../components/common/ProgressBar/ProgressBar';

export const IdleStatus: React.FC<IdleStatusProps> = ({ preview }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!preview || !preview.isIdle) return null;

  const phase = idlePhase(preview);
  const isBillable = phase.kind === IdlePhase.Billable;
  const usedFreeMinutes = Math.max(
    0,
    preview.freeMinutes - phase.freeMinutesRemaining,
  );

  // Idle is a "pending — please act" state, styled orange like a Preparing
  // connector (see renderHelpers: Preparing → orange.main).
  return (
    <Box
      sx={(theme: Theme) => {
        const accent = theme.palette.orange.main;
        return {
          width: '100%',
          padding: '16px',
          borderRadius: '12px',
          border: `1px solid ${accent}55`,
          background: `linear-gradient(135deg, ${accent}1A 0%, ${accent}05 100%)`,
        };
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            sx={(theme: Theme) => ({
              display: 'flex',
              color: theme.palette.orange.main,
              '& svg': { fontSize: 20 },
              // Gently pulse the clock to signal the idle clock is live/counting.
              animation: 'idlePulse 1.8s ease-in-out infinite',
              '@keyframes idlePulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.4, transform: 'scale(0.85)' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            })}
          >
            <AccessTimeFilledRoundedIcon />
          </Box>
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, color: 'common.white' }}
          >
            {isBillable ? t('orderPage.idle.feeAccruing') : t('orderPage.idle.title')}
          </Typography>
        </Stack>
        {/* Current elapsed idle time (refreshed by the 5s preview poll). */}
        <Typography
          sx={(theme: Theme) => ({
            flexShrink: 0,
            fontSize: 16,
            fontWeight: 700,
            color: theme.palette.orange.main,
          })}
        >
          {t('orderPage.idle.elapsed', { minutes: preview.totalMinutes })}
        </Typography>
      </Stack>

      <ProgressBar
        percent={phase.fillPercent}
        fillBackground={theme.palette.orange.main}
        shimmer={isBillable}
      />

      {isBillable ? (
        <Stack sx={{ mt: 1.5 }} spacing={0.5}>
          <Typography sx={{ fontSize: 13, color: 'gray.100' }}>
            {t('orderPage.idle.feeBreakdown', {
              minutes: phase.billableMinutes,
              price: phase.pricePerMinute.toFixed(2),
            })}
          </Typography>
          <Typography
            sx={(theme: Theme) => ({
              fontSize: 18,
              fontWeight: 700,
              color: theme.palette.orange.main,
            })}
          >
            {phase.amount.toFixed(2)} ₴
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: 'gray.100' }}>
            {t('orderPage.idle.freeTime', {
              used: usedFreeMinutes,
              total: preview.freeMinutes,
            })}
          </Typography>
          <Typography
            sx={(theme: Theme) => ({
              fontSize: 13,
              fontWeight: 600,
              color: theme.palette.orange.main,
            })}
          >
            {t('orderPage.idle.freeRemaining', {
              minutes: phase.freeMinutesRemaining,
            })}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
